import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import logoUrl from '../../../assets/images/xlrs-horz.svg'
import './Loader.scss'

/**
 * Branded loading indicator that evokes the Document AI OCR pipeline: a scan
 * beam sweeps across the logo while the caption shows a fixed label or cycles
 * through processing `stages`. Pass `DOCUMENT_SCAN_STAGES` for document
 * processing waits.
 */
type Props = {
  /** Fixed caption. Ignored when `stages` is provided. */
  label?: string
  /** Captions to cycle through (e.g. OCR stages). */
  stages?: string[]
  /** Render as a centred full-screen overlay. */
  fullscreen?: boolean
  className?: string
}

export const DOCUMENT_SCAN_STAGES = [
  'Scanning documents…',
  'Extracting text…',
  'Recognising fields…',
  'Structuring data…'
]

const STAGE_MS = 1400

const Loader: React.FC<Props> = ({
  label = 'Loading...',
  stages,
  fullscreen = false,
  className
}) => {
  const [stage, setStage] = useState(0)
  const cycling = !!stages?.length

  useEffect(() => {
    if (!cycling) return undefined
    const intervalId = setInterval(() => {
      setStage((s) => (s + 1) % (stages?.length ?? 1))
    }, STAGE_MS)
    return () => clearInterval(intervalId)
  }, [cycling, stages])

  return (
    <div
      className={classNames(
        'loader',
        { 'loader--fullscreen': fullscreen },
        className
      )}
      role="status"
      aria-live="polite"
    >
      <div className="loader__stage">
        <img className="loader__logo" src={logoUrl} alt="" />
        <span className="loader__beam" />
      </div>
      <p className="loader__caption">{cycling ? stages?.[stage] : label}</p>
    </div>
  )
}

export default Loader
